import { Component } from '@theme/component';
import { ThemeEvents, VariantUpdateEvent, CartAddEvent } from '@theme/events';
import { fetchConfig, prefersReducedMotion } from '@theme/utilities';
import { formatMoney } from '@theme/money-formatting';

/**
 * @typedef {object} PdpOptionValue
 * @property {string} value
 * @property {string} handle
 *
 * @typedef {object} PdpOption
 * @property {string} name
 * @property {string} handle
 * @property {number} index - 0-based option position
 * @property {PdpOptionValue[]} values
 *
 * @typedef {object} PdpVariant
 * @property {number} id
 * @property {string[]} options - Option values in position order
 * @property {number} price - In minor units (cents)
 * @property {number} compareAt - In minor units (cents); 0 when none
 * @property {boolean} available
 * @property {{ id: number, src: string } | null} media
 * @property {Array<{ plan: number, price: number }>} [allocs]
 *
 * @typedef {object} PdpData
 * @property {number} productId
 * @property {string} productTitle
 * @property {string} productUrl
 * @property {string} moneyFormat
 * @property {number} threshold - Free-shipping threshold in cents
 * @property {string} flatRate
 * @property {number} cartSubtotal - Cart subtotal at render time (cents)
 * @property {string} shipsFreeLabel
 * @property {{ free: string, progress: string, subscription: string, switch: string }} messages
 * @property {PdpOption[]} options - Empty for single-variant products
 * @property {PdpVariant[]} variants
 * @property {Array<{ id: number, name: string, percent: number }>} [plans]
 */

/**
 * @typedef {object} EverydayPdpRefs
 * @property {HTMLElement[]} [optionGroups]
 * @property {HTMLButtonElement[]} [optionChips]
 * @property {HTMLElement} [switchNote]
 * @property {HTMLElement} [purchaseType]
 * @property {HTMLInputElement[]} [purchaseRadios]
 * @property {HTMLSelectElement} [planSelect]
 * @property {HTMLElement} [shippingLine]
 * @property {HTMLElement} [shippingMsg]
 * @property {HTMLElement} [gapFillers]
 * @property {HTMLElement} [restockLink]
 */

/**
 * Generic option selector for the everyday PDP.
 *
 * The generalization of <tub-pdp> to any product shape: selection state is an
 * array of option values in position order (empty-ish for single-variant
 * products, two or three entries for matrix products). It owns selection and
 * drives the theme's native components — media gallery, sticky add-to-cart
 * bar, product form — by dispatching the theme's own `variant:update` event
 * with a freshly fetched document, exactly like the native variant picker
 * does (see docs/tub-pdp-recon.md). It never forks the theme's product form:
 * the form's hidden variant input and the URL are kept in sync synchronously,
 * so add-to-cart is always correct even while a re-render fetch is in flight.
 *
 * @extends {Component<EverydayPdpRefs>}
 */
class EverydayPdp extends Component {
  /** @type {PdpData} */
  #data;

  /** @type {string[]} - Selected option values in position order */
  #selections = [];

  /** @type {string | null} */
  #sellingPlanId = null;

  /** @type {number} */
  #qty = 1;

  /** @type {number} */
  #cartSubtotal = 0;

  /** @type {AbortController | undefined} */
  #fetchAbort;

  #listeners = new AbortController();

  connectedCallback() {
    super.connectedCallback();

    const dataScript = this.querySelector('script[data-pdp-data]');
    if (!dataScript?.textContent) return;
    this.#data = JSON.parse(dataScript.textContent);
    this.#cartSubtotal = this.#data.cartSubtotal || 0;

    // derive the initial selection from the form's variant input — the one
    // source of truth Liquid already resolved (selected_or_first_available)
    const idInput = /** @type {HTMLInputElement | null} */ (
      this.querySelector('product-form-component input[name="id"]')
    );
    const initial =
      this.#data.variants.find((v) => v.id === Number(idInput?.value)) ?? this.#data.variants[0];
    this.#selections = initial ? [...initial.options] : [];

    const { signal } = this.#listeners;
    document.addEventListener(ThemeEvents.cartUpdate, this.#onCartChanged, { signal });
    document.addEventListener(ThemeEvents.discountUpdate, this.#onCartChanged, { signal });
    this.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#onQuantityUpdate, { signal });
    this.addEventListener('submit', this.#onFormSubmit, { signal, capture: true });

    this.#initFromUrl();
    this.#refreshCartSubtotal();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#listeners.abort();
    this.#fetchAbort?.abort();
  }

  // ---------- variant matrix ----------

  /**
   * @param {string[]} selections
   * @returns {PdpVariant | undefined}
   */
  variantFor(selections) {
    return this.#data.variants.find((v) => v.options.every((value, i) => value === selections[i]));
  }

  /** @returns {PdpVariant | undefined} */
  get currentVariant() {
    return this.variantFor(this.#selections);
  }

  /**
   * Best variant carrying `value` at option `index`: fewest changes to the
   * other options, and sold-out combinations only when no in-stock one
   * exists — never dead-end the buyer when an available combo exists.
   * @param {number} index
   * @param {string} value
   * @returns {PdpVariant | undefined}
   */
  #bestVariantWith(index, value) {
    let best;
    let bestScore = Infinity;
    for (const v of this.#data.variants) {
      if (v.options[index] !== value) continue;
      const changes = v.options.reduce(
        (n, val, i) => (i !== index && val !== this.#selections[i] ? n + 1 : n),
        0
      );
      const score = changes * 2 + (v.available ? 0 : 100);
      if (score < bestScore) {
        bestScore = score;
        best = v;
      }
    }
    return best;
  }

  /** @param {number} index */
  #optionByIndex(index) {
    return this.#data.options.find((o) => o.index === index);
  }

  /** Display form for notes: parenthetical dropped. @param {string} value */
  #valueDisplay(value) {
    return value.split('(')[0].trim();
  }

  /** @param {number} cents */
  #money(cents) {
    const currency = window.Shopify?.currency?.active || 'USD';
    return formatMoney(Math.round(cents), this.#data.moneyFormat, currency);
  }

  // ---------- selection handlers (declarative on: attributes) ----------

  /** @param {Event} event */
  onChipTap(event) {
    const chip = /** @type {HTMLElement} */ (event.target)?.closest?.('.pdp-chip');
    if (!(chip instanceof HTMLElement)) return;
    const { optionIndex, value } = chip.dataset;
    if (value !== undefined && optionIndex !== undefined) {
      this.selectValue(Number(optionIndex), value);
    }
  }

  /** @param {Event} _event */
  onRestockClick(_event) {
    this.#track('pdp:restock_click');
  }

  /** @param {KeyboardEvent} event */
  onChipKeydown(event) {
    // the delegated event's target is the radiogroup (the on:keydown
    // carrier) — the chip being navigated from is the focused element
    const chip = document.activeElement instanceof Element ? document.activeElement.closest('.pdp-chip') : null;
    if (!(chip instanceof HTMLElement)) return;
    const index = chip.dataset.optionIndex;
    const items = (this.refs.optionChips ?? []).filter((c) => c.dataset.optionIndex === index);
    this.#radioKeydown(event, items, (el) => {
      const { optionIndex, value } = el.dataset;
      if (value !== undefined && optionIndex !== undefined) {
        this.selectValue(Number(optionIndex), value);
      }
    });
  }

  /**
   * Roving-tabindex arrow navigation for a radiogroup.
   * @param {KeyboardEvent} event
   * @param {HTMLElement[]} items
   * @param {(el: HTMLElement) => void} select
   */
  #radioKeydown(event, items, select) {
    if (items.length === 0) return;
    const current = items.indexOf(/** @type {HTMLElement} */ (document.activeElement));
    let next = -1;
    switch (event.key) {
      case 'ArrowRight':
      case 'ArrowDown':
        next = current < 0 ? 0 : (current + 1) % items.length;
        break;
      case 'ArrowLeft':
      case 'ArrowUp':
        next = current < 0 ? 0 : (current - 1 + items.length) % items.length;
        break;
      case 'Home':
        next = 0;
        break;
      case 'End':
        next = items.length - 1;
        break;
      default:
        return;
    }
    event.preventDefault();
    const target = items[next];
    if (!target) return;
    target.focus();
    select(target);
  }

  /**
   * Select a value for one option. If the combination doesn't exist with the
   * current other selections, hop to the closest variant that carries the
   * value and say what moved — never dead-end.
   * @param {number} index
   * @param {string} value
   */
  selectValue(index, value) {
    if (this.#selections[index] === value) return;
    let note = '';

    const candidate = [...this.#selections];
    candidate[index] = value;

    if (this.variantFor(candidate)) {
      this.#selections = candidate;
    } else {
      const best = this.#bestVariantWith(index, value);
      if (!best) return;
      const changes = best.options
        .map((val, i) =>
          i !== index && val !== this.#selections[i]
            ? `${this.#optionByIndex(i)?.name ?? ''} to ${this.#valueDisplay(val)}`.trim()
            : ''
        )
        .filter(Boolean)
        .join(', ');
      note = this.#data.messages.switch
        .replace('[changes]', changes)
        .replace('[value]', this.#valueDisplay(value));
      this.#selections = [...best.options];
    }

    this.#commit(note);
    this.#track('pdp:option_select', { option: this.#optionByIndex(index)?.name ?? '' });
  }

  // ---------- rendering ----------

  /**
   * Apply the current selection everywhere: optimistic UI first, then the
   * theme-native re-render via variant:update.
   * @param {string} [note]
   */
  #commit(note = '') {
    const variant = this.currentVariant;

    this.#renderChips();
    this.#renderPrice();
    this.#renderPurchaseType();
    this.#renderShipping();

    const noteEl = this.refs.switchNote;
    if (noteEl) noteEl.textContent = note;

    // keep the form correct synchronously — add-to-cart must never race the fetch
    const idInput = /** @type {HTMLInputElement | null} */ (
      this.querySelector('product-form-component input[name="id"]')
    );
    if (idInput && variant) idInput.value = String(variant.id);
    const atcButton = /** @type {HTMLButtonElement | null} */ (
      this.querySelector('product-form-component [ref="addToCartButton"]')
    );
    if (atcButton) atcButton.disabled = !variant || !variant.available;

    const restock = /** @type {HTMLElement | undefined} */ (this.refs.restockLink);
    if (restock) restock.hidden = !(variant && !variant.available);

    if (variant) {
      const url = new URL(window.location.href);
      for (const option of this.#data.options) url.searchParams.delete(option.handle);
      url.searchParams.set('variant', String(variant.id));
      history.replaceState({}, '', url.toString());
    }

    if (variant) this.#fetchAndDispatch(variant);
  }

  #renderChips() {
    for (const chip of this.refs.optionChips ?? []) {
      const index = Number(chip.dataset.optionIndex);
      const value = chip.dataset.value ?? '';
      const candidate = [...this.#selections];
      candidate[index] = value;
      const variant = this.variantFor(candidate);
      const state = !variant ? 'incompatible' : variant.available ? 'ok' : 'soldout';
      const selected = value === this.#selections[index];

      chip.dataset.state = state;
      chip.setAttribute('aria-checked', String(selected));
      chip.tabIndex = selected ? 0 : -1;

      const price = chip.querySelector('[data-ref="chipPrice"]');
      if (price) price.textContent = variant ? this.#money(variant.price) : '—';

      const each = chip.querySelector('[data-ref="chipEach"]');
      if (each) {
        const count = Number(chip.dataset.count) || 0;
        each.textContent = variant && count > 1 ? `${this.#money(variant.price / count)} ea` : '';
      }

      const micro = chip.querySelector('[data-ref="micro"]');
      if (micro) {
        micro.textContent =
          state === 'soldout'
            ? chip.dataset.soldoutText || 'Sold out'
            : state === 'incompatible'
              ? this.#comboNote(index, value)
              : chip.dataset.badge || '';
      }
    }
  }

  /**
   * "12 & 50 only" note for a value missing in some combinations — mirrors
   * the Liquid first paint. Only meaningful on two-option products.
   * @param {number} index
   * @param {string} value
   */
  #comboNote(index, value) {
    if (this.#data.options.length !== 2) return '';
    const other = this.#data.options.find((o) => o.index !== index);
    if (!other) return '';
    const valid = other.values.filter((val) =>
      this.#data.variants.some((v) => v.options[index] === value && v.options[other.index] === val.value)
    );
    if (valid.length === 0 || valid.length >= other.values.length) return '';
    const joined = valid.map((val) => val.value).join(' & ');
    return joined.length <= 24 ? `${joined} only` : `Limited ${other.name.toLowerCase()} options`;
  }

  #renderPrice() {
    const variant = this.currentVariant;
    if (!variant) return;

    const main = this.querySelector('[data-ref="mainPrice"]');
    if (main) {
      main.textContent = this.#money(variant.price);
      main.classList.toggle('pdp-price__current--sale', variant.compareAt > variant.price);
    }

    const compare = /** @type {HTMLElement | null} */ (this.querySelector('[data-ref="comparePrice"]'));
    if (compare) {
      const onSale = variant.compareAt > variant.price;
      compare.hidden = !onSale;
      compare.textContent = onSale ? this.#money(variant.compareAt) : '';
    }
  }

  // ---------- purchase type (selling plans) ----------

  /** Allocation price in cents for the current variant under a plan. @param {number} planId */
  #planPrice(planId) {
    const variant = this.currentVariant;
    if (!variant) return 0;
    const alloc = (variant.allocs ?? []).find((a) => a.plan === planId);
    if (alloc) return alloc.price;
    const plan = (this.#data.plans ?? []).find((p) => p.id === planId);
    if (plan?.percent) return Math.round(variant.price * (1 - plan.percent / 100));
    return variant.price;
  }

  #renderPurchaseType() {
    const variant = this.currentVariant;
    if (!this.refs.purchaseType || !variant) return;

    const oneTime = this.querySelector('[data-ref="oneTimePrice"]');
    if (oneTime) oneTime.textContent = this.#money(variant.price);

    const planId = this.#sellingPlanId
      ? Number(this.#sellingPlanId)
      : Number((this.refs.purchaseRadios ?? []).find((r) => r.value !== '')?.value ?? 0);
    const planPrice = this.querySelector('[data-ref="planPrice"]');
    if (planPrice && planId) planPrice.textContent = this.#money(this.#planPrice(planId));
  }

  /** @param {Event} event */
  onPlanChange(event) {
    const radio = /** @type {HTMLInputElement} */ (event.target);
    this.#sellingPlanId = radio.value || null;

    for (const other of this.refs.purchaseRadios ?? []) {
      const row = other.closest('.pdp-purchase__row');
      if (row instanceof HTMLElement) row.dataset.selected = String(other.checked);
    }

    this.#syncSellingPlanInput();
    this.#renderPurchaseType();
    this.#renderShipping();
    this.#track('pdp:plan_select', { sellingPlanId: this.#sellingPlanId });
  }

  /** @param {Event} event */
  onPlanIntervalChange(event) {
    const select = /** @type {HTMLSelectElement} */ (event.target);
    const subscribeRadio = (this.refs.purchaseRadios ?? []).find((r) => r.value !== '');
    if (subscribeRadio) {
      subscribeRadio.value = select.value;
      if (subscribeRadio.checked) this.#sellingPlanId = select.value;
    }
    this.#syncSellingPlanInput();
    this.#renderPurchaseType();
    this.#track('pdp:plan_select', { sellingPlanId: this.#sellingPlanId });
  }

  #syncSellingPlanInput() {
    const input = /** @type {HTMLInputElement | null} */ (this.querySelector('[data-pdp-selling-plan]'));
    if (!input) return;
    if (this.#sellingPlanId) {
      input.value = this.#sellingPlanId;
      input.disabled = false;
    } else {
      input.value = '';
      input.disabled = true;
    }
  }

  // ---------- shipping line + gap filler ----------

  #renderShipping() {
    const { shippingMsg, gapFillers, shippingLine } = this.refs;
    if (!shippingMsg) return;

    if (this.#sellingPlanId) {
      shippingMsg.textContent = this.#data.messages.subscription;
      if (gapFillers) gapFillers.hidden = true;
      if (shippingLine) shippingLine.dataset.under = 'false';
      return;
    }

    const variant = this.currentVariant;
    const selectionTotal = (variant ? variant.price : 0) * this.#qty;
    const gap = this.#data.threshold - (selectionTotal + this.#cartSubtotal);
    const under = gap > 0;

    shippingMsg.textContent = under
      ? this.#data.messages.progress.replace('[amount]', this.#money(gap)).replace('[flat]', this.#data.flatRate)
      : this.#data.messages.free;
    if (gapFillers) gapFillers.hidden = !under;
    if (shippingLine) shippingLine.dataset.under = String(under);
  }

  /** @param {Event} _event */
  #onCartChanged = (_event) => {
    const event = /** @type {CustomEvent} */ (_event);
    const total = event.detail?.resource?.total_price;
    if (typeof total === 'number') {
      this.#cartSubtotal = total;
      this.#renderShipping();
    } else {
      this.#refreshCartSubtotal();
    }
  };

  async #refreshCartSubtotal() {
    try {
      const response = await fetch(`${Theme.routes.cart_url}.js`);
      const cart = await response.json();
      if (typeof cart.total_price === 'number') {
        this.#cartSubtotal = cart.total_price;
        this.#renderShipping();
      }
    } catch {
      /* shipping line simply keeps its last state */
    }
  }

  /** One-tap add for gap-filler and cross-sell chips. @param {Event} event */
  async onQuickAdd(event) {
    const button = /** @type {HTMLButtonElement} */ (event.target);
    const variantId = button.dataset.variantId;
    if (!variantId || button.disabled) return;
    button.disabled = true;

    try {
      const formData = new FormData();
      formData.set('id', variantId);
      formData.set('quantity', '1');
      const cartItemsSections = Array.from(document.querySelectorAll('cart-items-component'))
        .map((el) => (el instanceof HTMLElement ? el.dataset.sectionId : null))
        .filter(Boolean);
      if (cartItemsSections.length > 0) formData.set('sections', cartItemsSections.join(','));

      const fetchCfg = fetchConfig('javascript', { body: formData });
      const response = await fetch(Theme.routes.cart_add_url, {
        ...fetchCfg,
        headers: { ...fetchCfg.headers, Accept: 'text/html' },
      });
      const result = await response.json();
      if (result.status) throw new Error(result.message || 'could not add to cart');

      const cart = await (await fetch(`${Theme.routes.cart_url}.js`)).json();
      this.#cartSubtotal = cart.total_price ?? this.#cartSubtotal;

      this.dispatchEvent(
        new CartAddEvent(cart, this.id || 'everyday-pdp', {
          source: 'everyday-pdp',
          itemCount: cart.item_count,
          variantId,
          sections: result.sections,
        })
      );

      button.dataset.added = 'true';
      setTimeout(() => {
        button.removeAttribute('data-added');
        button.disabled = false;
      }, 2000);

      this.#renderShipping();
      this.#track('pdp:quickadd', {
        addedVariantId: Number(variantId),
        addedPrice: Number(button.dataset.price) / 100,
        context: button.dataset.context || 'gap-filler',
      });
    } catch (error) {
      console.error('everyday-pdp: quick add failed', error);
      button.disabled = false;
    }
  }

  // ---------- theme-native re-render ----------

  /**
   * Fetch the page for the resolved variant and dispatch the theme's
   * variant:update so the native gallery, sticky bar and product form morph
   * themselves — same contract as the native variant picker.
   * @param {PdpVariant} variant
   */
  async #fetchAndDispatch(variant) {
    this.#fetchAbort?.abort();
    this.#fetchAbort = new AbortController();

    const resource = {
      id: variant.id,
      available: variant.available,
      featured_media: variant.media ? { id: variant.media.id, preview_image: { src: variant.media.src } } : null,
    };

    let html;
    try {
      const url = `${this.#data.productUrl.split('?')[0]}?variant=${variant.id}`;
      const response = await fetch(url, { signal: this.#fetchAbort.signal });
      html = new DOMParser().parseFromString(await response.text(), 'text/html');
    } catch (error) {
      if (error instanceof DOMException && error.name === 'AbortError') return;
      // still dispatch so listeners sync from the resource; empty doc no-ops the morphs
      html = new DOMParser().parseFromString('', 'text/html');
    }

    this.dispatchEvent(
      new VariantUpdateEvent(resource, String(variant.id), {
        html,
        productId: this.dataset.productId ?? '',
      })
    );

    if (!variant.media) this.#syncGalleryToSelection();
  }

  /**
   * Alt-text fallback media matching: when the variant has no featured media,
   * select the gallery slide whose image alt carries
   * "<option-handle>:<value-handle>" (e.g. "flavor:caramel", "color:blue") —
   * the tag can ride at the end of a descriptive, SEO-friendly alt text.
   * Same convention as the tub PDP (docs/tub-pdp-recon.md §8), extended to
   * any option.
   */
  #syncGalleryToSelection() {
    const gallery = /** @type {any} */ (this.querySelector('media-gallery'));
    const slideshow = gallery?.slideshow;
    const slides = slideshow?.refs?.slides;
    if (!slides || slides.length === 0) return;

    for (const option of this.#data.options) {
      const selected = this.#selections[option.index];
      const handle = option.values.find((val) => val.value === selected)?.handle;
      if (!handle) continue;
      const tag = `${option.handle}:${handle}`;

      const index = Array.from(slides).findIndex((slide) => {
        const img = /** @type {HTMLImageElement | null} */ (slide.querySelector('img[alt]'));
        return img ? img.alt.trim().toLowerCase().includes(tag) : false;
      });

      if (index >= 0) {
        slideshow.select(index, undefined, { animate: !prefersReducedMotion() });
        return;
      }
    }
  }

  // ---------- URL contract ----------

  /**
   * On load: `?variant=` is already resolved server-side (legacy links keep
   * working). Otherwise any `?<option-handle>=<value-handle>` params (e.g.
   * ?flavor=caramel) are matched; an invalid combination hops to the nearest
   * valid variant carrying the last requested value.
   */
  #initFromUrl() {
    const params = new URLSearchParams(window.location.search);
    if (params.get('variant')) return;

    const candidate = [...this.#selections];
    let lastRequested = null;
    for (const option of this.#data.options) {
      const param = params.get(option.handle);
      if (!param) continue;
      const match = option.values.find((val) => val.handle === param.toLowerCase());
      if (!match) continue;
      candidate[option.index] = match.value;
      lastRequested = { index: option.index, value: match.value };
    }
    if (!lastRequested) return;

    if (this.variantFor(candidate)) {
      if (candidate.every((value, i) => value === this.#selections[i])) return;
      this.#selections = candidate;
    } else {
      const best = this.#bestVariantWith(lastRequested.index, lastRequested.value);
      if (!best) return;
      this.#selections = [...best.options];
    }
    this.#commit('');
  }

  // ---------- quantity + analytics ----------

  /** @param {Event} _event */
  #onQuantityUpdate = (_event) => {
    const event = /** @type {CustomEvent} */ (_event);
    if (event.detail?.cartLine) return;
    this.#qty = Number(event.detail?.quantity) || 1;
    this.#renderShipping();
  };

  /** @param {Event} event */
  #onFormSubmit = (event) => {
    if (!(event.target instanceof HTMLFormElement)) return;
    this.#track('pdp:atc', { sellingPlanId: this.#sellingPlanId });
  };

  /**
   * Dispatch an analytics CustomEvent and mirror it into the dataLayer.
   * @param {string} name
   * @param {Record<string, unknown>} [extra]
   */
  #track(name, extra = {}) {
    const variant = this.currentVariant;
    /** @type {Record<string, string>} */
    const selections = {};
    for (const option of this.#data.options) {
      selections[option.name] = this.#selections[option.index] ?? '';
    }
    const detail = {
      product: this.#data.productTitle,
      selections,
      variantId: variant?.id ?? null,
      price: variant ? variant.price / 100 : null,
      quantity: this.#qty,
      ...extra,
    };
    document.dispatchEvent(new CustomEvent(name, { detail, bubbles: true }));
    if (Array.isArray(window.dataLayer)) {
      window.dataLayer.push({ event: name, ...detail });
    }
  }
}

if (!customElements.get('everyday-pdp')) {
  customElements.define('everyday-pdp', EverydayPdp);
}
